import { Button } from '@mui/material';
import Typography from '@mui/material/Typography';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useGlobalCore } from '../../../../core/modules/global';
import { CREATE_STORE_DIALOG } from '../../../../core/constants/dialog';
import { useCompanyCore } from '../../../../core/modules/company';
import { useMeCore } from '../../../../core/modules/me';
import { useMasterCore } from '../../../../core/modules/master';
import { useWarehouseCore } from '../../../../core/modules/warehouse';
import { FilterDrawer, FilterField } from '../../../components/shared/FilterDrawer';

const Header = () => {
  const { openDialog } = useGlobalCore();
  const { companyId } = useCompanyCore();
  const { user } = useMeCore();
  const { getTypes, channelTypes } = useMasterCore();
  const { getWarehouses } = useWarehouseCore();

  const [filters, setFilters] = useState<{ code?: string; description?: string; channel_type_id?: string }>({});
  const [loading, setLoading] = useState(false);

  const canWrite = useMemo(() => user && (user.privileges.superAdmin || user.privileges.writeWarehouse), [user]);

  const openCreateDialog = useCallback(() => {
    if (companyId) {
      getTypes(companyId, 'warehouse');
      getTypes(companyId, 'channel');
    }
    openDialog(CREATE_STORE_DIALOG);
  }, [companyId, getTypes, openDialog]);

  // Cargar tipos de canal al montar el componente
  useEffect(() => {
    if (companyId) {
      getTypes(companyId, 'channel');
    }
  }, [companyId, getTypes]);

  const handleFiltersChange = useCallback((newFilters: { code?: string; description?: string; channel_type_id?: string }) => {
    setFilters(newFilters);
  }, []);

  const handleApplyFilters = useCallback((appliedFilters?: { code?: string; description?: string; channel_type_id?: string }) => {
    if (companyId) {
      setLoading(true);
      const filtersToApply = appliedFilters || filters;
      if (appliedFilters) setFilters(appliedFilters);
      getWarehouses(companyId, filtersToApply, () => setLoading(false), () => setLoading(false));
    }
  }, [companyId, filters, getWarehouses]);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    if (companyId) {
      setLoading(true);
      getWarehouses(companyId, {}, () => setLoading(false), () => setLoading(false));
    }
  }, [companyId, getWarehouses]);

  const filterFields: FilterField[] = [
    { key: 'code', label: 'Código', type: 'text', placeholder: 'Buscar por código' },
    { key: 'description', label: 'Descripción', type: 'text', placeholder: 'Buscar por descripción' },
    { key: 'channel_type_id', label: 'Canal', type: 'select', options: channelTypes.map(c => ({ id: c.id, name: c.name, description: c.name })) },
  ];

  return (
    <div className="p-24 sm:p-32 w-full border-b-1 bg-white header-components">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex flex-col items-center sm:items-start">
          <Typography className="text-24 md:text-32 font-extrabold tracking-tight leading-none">
            Bodegas
          </Typography>
        </div>
        <div className="flex w-full sm:w-auto items-center gap-4 mt-4 sm:mt-0">
          <FilterDrawer
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
            fields={filterFields}
            loading={loading}
            disabled={!companyId}
          />
          {canWrite && (
            <Button
              variant="contained"
              color="secondary"
              disabled={!companyId}
              onClick={openCreateDialog}
              sx={{ borderRadius: '25px', height: 44, minWidth: 100, fontWeight: 700 }}
            >
              <AddCircleOutlineOutlinedIcon />
              <span className="mx-8">Crear</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
